/**
 * DateTimeStack - Compact, three-line timestamp used as the leading "Date"
 * cell on the Work Order and Inspection list rows.
 *
 * Renders a single timestamp stacked as:
 *	 DAY              (weekday abbreviation, e.g. "SUN" — small label, 0.5em)
 *	 MM/DD/YY         (the anchor date, at the inherited cell size)
 *	 HH:MMam/pm EST   (the time, pinned to Eastern, 0.7em)
 *
 * Date-only values — bare "YYYY-MM-DD" strings or the midnight-UTC form DATE
 * columns arrive in (e.g. "2026-08-17T00:00:00.000000Z") — carry no real
 * clock time. They render as written, without timezone conversion (which
 * would roll the calendar back to the previous evening in Eastern), and the
 * meaningless time line is omitted.
 *
 * The em-based sizes scale relative to whatever font-size the cell inherits, so
 * the three lines stay proportional wherever the stack is dropped in. Time and
 * weekday are pinned to America/New_York so they always match the "EST" label.
 *
 * Presentation-only — give it a Date or ISO string and it formats; it owns no
 * domain logic.
 *
 * @example
 * <DateTimeStack date={wo.scheduledDate || wo.createdAt} />
 */
import React from 'react';

/* IANA zone the time + weekday are pinned to, matching the "EST" label. */
const TIME_ZONE = 'America/New_York';

/* The shapes DATE (no time-of-day) columns arrive in from the API: a bare
 * "2026-08-17" or a midnight-UTC timestamp like "2026-08-17T00:00:00.000000Z"
 * (offset form "+00:00" and zoneless "T00:00:00" included). */
const DATE_ONLY_ISO = /^(\d{4}-\d{2}-\d{2})(?:T00:00:00(?:\.0+)?(?:Z|\+00:00)?)?$/;

/**
 * Returns the "YYYY-MM-DD" calendar date when the input is date-only — a bare
 * date string or a midnight-UTC instant — or null for real timestamps.
 */
function getDateOnlyYmd(value: Date | string): string | null {
	if (typeof value === 'string') {
		const match = DATE_ONLY_ISO.exec(value);
		return match ? match[1] : null;
	}
	const isMidnightUtc =
		value.getUTCHours() === 0 &&
		value.getUTCMinutes() === 0 &&
		value.getUTCSeconds() === 0 &&
		value.getUTCMilliseconds() === 0;
	return isMidnightUtc ? value.toISOString().slice(0, 10) : null;
}

export interface DateTimeStackProps {
	/** A Date object or ISO 8601 date string from the API. */
	date: Date | string | undefined | null;
	/** Optional extra classes appended after the block + base classes. */
	className?: string;
}

export const DateTimeStack: React.FC<DateTimeStackProps> = ({ date, className = '' }) => {
	// Guard against undefined/null timestamps from incomplete records.
	if (!date) {
		return (
			<span className={`date-time-stack text-brand-text-muted ${className}`} data-ref="atom-date-time-stack">—</span>
		);
	}

	// Date-only inputs re-parse at *local* midnight so the written calendar
	// date survives; real timestamps keep instant semantics.
	const dateOnlyYmd = getDateOnlyYmd(date);
	const d = dateOnlyYmd
		? new Date(`${dateOnlyYmd}T00:00:00`)
		: typeof date === 'string' ? new Date(date) : date;
	// A local-midnight parse already carries the calendar date — pinning it to
	// Eastern would shift it back a day for viewers west of Greenwich.
	const zone = dateOnlyYmd ? undefined : TIME_ZONE;

	// DAY — short weekday uppercased (e.g. "SUN"), pinned to Eastern.
	const day = d.toLocaleDateString('en-US', { weekday: 'short', timeZone: zone }).toUpperCase();
	// MM/DD/YY — 2-digit month / day / year.
	const dateLabel = d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit', timeZone: zone });
	// HH:MMam/pm — lowercase meridiem, no space (e.g. "3:30pm"), Eastern time.
	// Null for date-only values: midnight is an artifact of the storage format,
	// not a real scheduled time.
	const time = dateOnlyYmd
		? null
		: d
			.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: zone })
			.replace(' ', '')
			.toLowerCase();

	return (
		<div className={`date-time-stack flex flex-col leading-tight text-brand-text whitespace-nowrap ${className}`} data-ref="atom-date-time-stack">
			{/* Weekday label — 0.5em relative to the inherited cell font-size. */}
			<span className="date-time-stack__day text-[0.5em] font-semibold uppercase tracking-wide text-brand-text-subtle">{day}</span>
			{/* Anchor date — renders at the cell's inherited size. */}
			<span className="date-time-stack__date font-medium">{dateLabel}</span>
			{/* Time + zone — 0.7em relative to the inherited cell font-size.
			    Omitted for date-only values, which have no real clock time. */}
			{time && <span className="date-time-stack__time text-[0.7em] text-brand-text-muted">{time} EST</span>}
		</div>
	);
};

export default DateTimeStack;
